import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronLeft, ChevronRight, Search, Send, UserPlus } from 'lucide-react';
import { FormEvent, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/Button';
import { Dialog, DialogContent, DialogTrigger } from '../components/ui/Dialog';
import { Field, Input, Select } from '../components/ui/Field';
import {
  ApiError,
  inviteClient,
  listClients,
  listSubscriptionPlans,
  resendClientInvitation,
  type BillingInterval,
  type ClientSearchField,
  type ClientSort,
  type ClientSummary,
  type ClientView,
  type InviteClientInput,
  type SubscriptionPlan,
  type SubscriptionPlanId
} from '../lib/api';
import { useAuth } from '../lib/auth';
import { cn, formatBytes, formatDate } from '../lib/utils';

export type { ClientSort, ClientView };

const STALE_THRESHOLDS = [14, 30, 90] as const;

export function daysSince(value: string | undefined, now = new Date()): number | null {
  if (!value) return null;
  return Math.max(0, Math.floor((now.getTime() - new Date(value).getTime()) / 86_400_000));
}

export function highStorageClientIds(clients: ClientSummary[]): Set<string> {
  const stored = clients.filter((client) => client.mediaBytes > 0).sort((a, b) => b.mediaBytes - a.mediaBytes);
  const count = stored.length ? Math.max(1, Math.ceil(stored.length * 0.1)) : 0;
  return new Set(stored.slice(0, count).map((client) => client.clientId));
}

export function getClientHealth(client: ClientSummary, highStorageIds: Set<string>, now = new Date()) {
  const staleDays = daysSince(client.lastSeenAt, now);
  const inactiveStatus = client.health?.inactiveStatus ?? (client.suspended || !client.enabled);
  const noGalleries = client.health?.noGalleries ?? client.galleriesTotal === 0;
  const noMedia = client.health?.noMedia ?? (client.mediaTotal ?? client.photosTotal + (client.videosTotal ?? 0)) === 0;
  const neverActive = client.health?.neverActive ?? !client.lastSeenAt;
  const highStorage = client.health?.highStorage ?? highStorageIds.has(client.clientId);
  return {
    staleDays,
    stale: staleDays !== null && staleDays >= STALE_THRESHOLDS[0],
    inactiveStatus,
    noGalleries,
    noMedia,
    neverActive,
    highStorage,
    needsAttention: client.health?.needsAttention ?? (inactiveStatus || noGalleries || noMedia || highStorage || (staleDays !== null && staleDays >= STALE_THRESHOLDS[0]))
  };
}

export function compareClients(a: ClientSummary, b: ClientSummary, sort: ClientSort): number {
  if (sort === 'activity') {
    if (a.lastSeenAt && b.lastSeenAt) return b.lastSeenAt.localeCompare(a.lastSeenAt) || compareNames(a, b);
    if (a.lastSeenAt) return -1;
    if (b.lastSeenAt) return 1;
    return (b.firstAppSeenAt ?? b.signedUpAt ?? '').localeCompare(a.firstAppSeenAt ?? a.signedUpAt ?? '') || compareNames(a, b);
  }
  if (sort === 'name') return compareNames(a, b);
  if (sort === 'storage') return b.mediaBytes - a.mediaBytes || compareNames(a, b);
  if (sort === 'galleries') return b.galleriesTotal - a.galleriesTotal || compareNames(a, b);
  return b.exportRequests - a.exportRequests || compareNames(a, b);
}

function compareNames(a: ClientSummary, b: ClientSummary): number {
  return a.displayName.localeCompare(b.displayName) || a.email.localeCompare(b.email);
}

export function clientMatchesView(client: ClientSummary, view: ClientView, highStorageIds: Set<string>, now = new Date()): boolean {
  const health = getClientHealth(client, highStorageIds, now);
  if (view === 'all') return true;
  if (view === 'attention') return health.needsAttention;
  if (view === 'never-active') return health.neverActive;
  if (view === 'no-galleries') return health.noGalleries;
  if (view === 'no-media') return health.noMedia;
  if (view === 'inactive-status') return health.inactiveStatus;
  if (view === 'high-storage') return health.highStorage;
  return health.staleDays !== null && health.staleDays >= Number(view.replace('stale-', ''));
}

export function ClientsPage() {
  const { token } = useAuth();
  const queryClient = useQueryClient();
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [searchField, setSearchField] = useState<ClientSearchField>('email');
  const [view, setView] = useState<ClientView>('all');
  const [sort, setSort] = useState<ClientSort>('activity');
  const [limit, setLimit] = useState<25 | 50 | 100>(25);
  const [cursors, setCursors] = useState<Array<string | undefined>>([undefined]);

  useEffect(() => {
    const timer = window.setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => window.clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => setCursors([undefined]), [limit, search, searchField, sort, view]);
  const cursor = cursors.at(-1);
  const clientsQuery = useQuery({
    queryKey: ['clients', { cursor, limit, search, searchField, sort, view }],
    queryFn: () => listClients(token!, { cursor, limit, query: search || undefined, searchField, sort, view })
  });
  const plansQuery = useQuery({ queryKey: ['subscription-plans'], queryFn: () => listSubscriptionPlans(token!) });
  const clients = clientsQuery.data?.clients ?? [];
  const metrics = clientsQuery.data?.metrics;
  const pageHighStorageIds = useMemo(() => highStorageClientIds(clients), [clients]);

  return (
    <section className="grid gap-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-normal">Clients</h1>
          <p className="mt-1 text-sm text-ink/70">
            {metrics ? `${metrics.clients} accounts, ${metrics.activeGalleries} active galleries, ${metrics.photos} photos, ${metrics.videos} videos, ${metrics.media} total media, ${formatBytes(metrics.bytes)} stored` : 'Loading client overview'}
          </p>
        </div>
        <InviteClientDialog
          token={token!}
          plans={plansQuery.data?.plans ?? []}
          onChanged={() => queryClient.invalidateQueries({ queryKey: ['clients'] })}
        />
      </div>

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
        <HealthCard label="All clients" value={metrics?.clients ?? 0} detail="Photographer accounts" />
        <HealthCard label="Needs attention" value={metrics?.health.attention ?? 0} detail="Onboarding, status, storage, or activity" tone="clay" />
        <HealthCard label="Never active" value={metrics?.health.neverActive ?? 0} detail="No recorded app activity" />
        <HealthCard label="Stale 14+ days" value={metrics?.health.stale ?? 0} detail="No recent app activity" />
        <HealthCard label="Suspended / disabled" value={metrics?.health.inactiveStatus ?? 0} detail="Account access is restricted" tone="clay" />
      </div>

      <div className="grid gap-3 rounded-sm bg-white p-4 shadow-sm ring-1 ring-ink/10 lg:grid-cols-[minmax(0,1fr)_180px_220px_180px] lg:items-end">
        <div className="grid gap-2 sm:grid-cols-[140px_minmax(0,1fr)]">
          <Select aria-label="Search field" value={searchField} onChange={(event) => setSearchField(event.target.value as ClientSearchField)}>
            <option value="email">Email</option>
            <option value="name">Name</option>
          </Select>
          <label className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink/70" />
            <Input className="pl-9" placeholder={`Search by ${searchField}`} value={searchInput} onChange={(event) => setSearchInput(event.target.value)} />
          </label>
        </div>
        <Field label="View">
          <Select id="client-view" value={view} onChange={(event) => setView(event.target.value as ClientView)}>
            <option value="all">All clients</option><option value="attention">Needs attention</option><option value="never-active">Never active</option>
            <option value="no-galleries">No galleries</option><option value="no-media">No media</option><option value="stale-14">Stale 14+ days</option>
            <option value="stale-30">Stale 30+ days</option><option value="stale-90">Stale 90+ days</option><option value="inactive-status">Suspended or disabled</option><option value="high-storage">High storage</option>
          </Select>
        </Field>
        <Field label="Sort">
          <Select id="client-sort" value={sort} disabled={Boolean(search)} onChange={(event) => setSort(event.target.value as ClientSort)}>
            <option value="activity">Most recently active</option><option value="name">Name</option><option value="storage">Storage</option><option value="galleries">Galleries</option><option value="downloads">Downloads</option>
          </Select>
        </Field>
        <Field label="Rows per page">
          <Select value={limit} onChange={(event) => setLimit(Number(event.target.value) as 25 | 50 | 100)}>
            <option value="25">25</option><option value="50">50</option><option value="100">100</option>
          </Select>
        </Field>
      </div>

      <div className="overflow-x-auto rounded-sm bg-white shadow-sm ring-1 ring-ink/10">
        <table className="admin-table w-full border-collapse text-left text-sm">
          <thead className="bg-smoke text-xs uppercase text-ink/70"><tr>
            {['Client', 'Status', 'Plan', 'Galleries', 'Photos', 'Videos', 'Media', 'Downloads', 'Last seen'].map((label) => <th key={label} className="px-4 py-3 font-semibold">{label}</th>)}
          </tr></thead>
          <tbody className="divide-y divide-ink/10">
            {clients.map((client) => <ClientRow key={client.clientId} client={client} highStorageIds={pageHighStorageIds} />)}
            {clients.length === 0 ? <tr><td colSpan={9} className="px-4 py-10 text-center text-sm text-ink/70">{clientsQuery.isLoading ? 'Loading clients' : clientsQuery.isError ? 'Could not load clients' : 'No clients found'}</td></tr> : null}
          </tbody>
        </table>
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm text-ink/65">Page {cursors.length} · Showing {clients.length} clients{metrics?.generatedAt ? ` · Overview updated ${formatDate(metrics.generatedAt)}` : ''}</p>
        <div className="flex gap-2">
          <Button variant="secondary" disabled={cursors.length === 1 || clientsQuery.isFetching} onClick={() => setCursors((current) => current.slice(0, -1))}><ChevronLeft className="h-4 w-4" /> Previous</Button>
          <Button variant="secondary" disabled={!clientsQuery.data?.nextCursor || clientsQuery.isFetching} onClick={() => setCursors((current) => [...current, clientsQuery.data!.nextCursor])}>Next <ChevronRight className="h-4 w-4" /></Button>
        </div>
      </div>
    </section>
  );
}

function InviteClientDialog({ token, plans, onChanged }: { token: string; plans: SubscriptionPlan[]; onChanged(): void }) {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [planId, setPlanId] = useState<SubscriptionPlanId>('free');
  const [billingInterval, setBillingInterval] = useState<BillingInterval>('none');
  const [customPrice, setCustomPrice] = useState('0');
  const [storageGb, setStorageGb] = useState('500');
  const [activeGalleries, setActiveGalleries] = useState('');
  const [photosPerGallery, setPhotosPerGallery] = useState('2000');
  const [success, setSuccess] = useState('');
  const selectedPlan = plans.find((plan) => plan.planId === planId);
  const inviteMutation = useMutation({ mutationFn: (input: InviteClientInput) => inviteClient(token, input), onSuccess: () => { setSuccess(`Invitation sent to ${email.trim().toLowerCase()}.`); onChanged(); } });
  const existingError = inviteMutation.error instanceof ApiError ? inviteMutation.error : null;
  const existingClientId = typeof existingError?.details.clientId === 'string' ? existingError.details.clientId : undefined;
  const canResend = existingError?.details.canResend === true;
  const resendMutation = useMutation({ mutationFn: () => resendClientInvitation(token, existingClientId!), onSuccess: () => { setSuccess(`Invitation resent to ${email.trim().toLowerCase()}.`); onChanged(); } });

  function choosePlan(next: SubscriptionPlanId) {
    setPlanId(next);
    const plan = plans.find((candidate) => candidate.planId === next);
    setBillingInterval(plan?.pricingMode === 'fixed' ? 'monthly' : plan?.pricingMode === 'quote' ? 'custom' : 'none');
  }

  function submit(event: FormEvent) {
    event.preventDefault();
    setSuccess('');
    inviteMutation.mutate({
      email: email.trim(), displayName: displayName.trim(), planId, billingInterval,
      ...(planId === 'enterprise' ? { customPriceCents: Math.round(Number(customPrice) * 100), customLimits: { storageBytes: Math.round(Number(storageGb) * 1_000_000_000), activeGalleries: activeGalleries ? Number(activeGalleries) : undefined, photosPerGallery: Number(photosPerGallery) } } : {})
    });
  }

  return <Dialog open={open} onOpenChange={(next) => { setOpen(next); if (!next) { inviteMutation.reset(); resendMutation.reset(); setSuccess(''); } }}>
    <DialogTrigger asChild><Button><UserPlus className="h-4 w-4" /> Invite client</Button></DialogTrigger>
    <DialogContent title="Invite a client" description="Create their account, choose the package, and email a seven-day temporary password.">
      <form className="grid gap-4" onSubmit={submit}>
        <div className="grid gap-4 sm:grid-cols-2"><Field label="Business or display name"><Input required maxLength={100} value={displayName} onChange={(event) => setDisplayName(event.target.value)} /></Field><Field label="Email"><Input required type="email" value={email} onChange={(event) => setEmail(event.target.value)} /></Field></div>
        <div className="grid gap-4 sm:grid-cols-2"><Field label="Plan"><Select value={planId} onChange={(event) => choosePlan(event.target.value as SubscriptionPlanId)}>{plans.map((plan) => <option key={plan.planId} value={plan.planId}>{plan.name}</option>)}</Select></Field><Field label="Billing"><Select value={billingInterval} disabled={selectedPlan?.pricingMode !== 'fixed'} onChange={(event) => setBillingInterval(event.target.value as BillingInterval)}><option value="none">None</option><option value="monthly">Monthly</option><option value="annual">Annual</option><option value="custom">Custom</option></Select></Field></div>
        {planId === 'enterprise' ? <div className="grid gap-4 sm:grid-cols-2"><Field label="Price (rand)"><Input type="number" min="0" step="0.01" value={customPrice} onChange={(event) => setCustomPrice(event.target.value)} /></Field><Field label="Storage (GB)"><Input type="number" min="500" value={storageGb} onChange={(event) => setStorageGb(event.target.value)} /></Field><Field label="Active galleries" hint="Leave blank for unlimited"><Input type="number" min="1" value={activeGalleries} onChange={(event) => setActiveGalleries(event.target.value)} /></Field><Field label="Photos per gallery"><Input type="number" min="1" max="2000" value={photosPerGallery} onChange={(event) => setPhotosPerGallery(event.target.value)} /></Field></div> : null}
        {success ? <p className="rounded-sm bg-moss/10 p-3 text-sm font-medium text-moss" role="status">{success}</p> : null}
        {inviteMutation.error && !success ? <div className="rounded-sm bg-clay/10 p-3 text-sm text-clay" role="alert"><p>{inviteMutation.error.message}</p>{existingClientId ? <Link className="mt-2 inline-block font-semibold underline" to={`/clients/${existingClientId}`}>Open existing client</Link> : null}</div> : null}
        {resendMutation.error ? <p className="text-sm text-clay" role="alert">{resendMutation.error.message}</p> : null}
        <div className="flex flex-wrap justify-end gap-2">{canResend && existingClientId ? <Button type="button" variant="secondary" disabled={resendMutation.isPending} onClick={() => resendMutation.mutate()}><Send className="h-4 w-4" /> {resendMutation.isPending ? 'Resending…' : 'Resend invitation'}</Button> : null}<Button type="submit" disabled={inviteMutation.isPending || plans.length === 0}>{inviteMutation.isPending ? 'Creating…' : 'Create and invite'}</Button></div>
      </form>
    </DialogContent>
  </Dialog>;
}

function ClientRow({ client, highStorageIds }: { client: ClientSummary; highStorageIds: Set<string> }) {
  const health = getClientHealth(client, highStorageIds);
  return <tr className="hover:bg-smoke/65">
    <td className="px-4 py-3"><Link to={`/clients/${client.clientId}`} className="font-medium text-ink hover:underline">{client.displayName}</Link><div className="mt-1 text-xs text-ink/70">{client.email}</div>{client.slug ? <div className="mt-1 text-xs text-ink/70">/{client.slug}</div> : null}<div className="mt-2 flex max-w-sm flex-wrap gap-1.5">{health.neverActive ? <HealthBadge>Never active</HealthBadge> : null}{health.noGalleries ? <HealthBadge>No galleries</HealthBadge> : null}{health.noMedia ? <HealthBadge>No media</HealthBadge> : null}{health.staleDays !== null && health.staleDays >= 14 ? <HealthBadge tone="clay">Stale {health.staleDays}d</HealthBadge> : null}{health.highStorage ? <HealthBadge>High storage</HealthBadge> : null}</div></td>
    <td className="px-4 py-3"><StatusBadge suspended={client.suspended} enabled={client.enabled} pending={client.userStatus === 'FORCE_CHANGE_PASSWORD'} /></td>
    <td className="px-4 py-3">{client.subscription ? <div className="grid gap-1"><span className="w-fit rounded-full bg-ink/10 px-2.5 py-1 text-xs font-semibold">{client.subscription.plan.name}</span>{client.subscription.subscription.pendingChangeRequest ? <span className="text-xs font-semibold text-clay">Change pending</span> : null}</div> : <span className="text-xs font-semibold text-clay">Not configured</span>}</td>
    <td className="px-4 py-3">{client.galleriesActive}/{client.galleriesTotal}</td><td className="px-4 py-3">{client.photosTotal.toLocaleString('en-ZA')}</td><td className="px-4 py-3">{(client.videosTotal ?? 0).toLocaleString('en-ZA')}</td><td className="px-4 py-3">{formatBytes(client.mediaBytes)}</td><td className="px-4 py-3">{client.exportRequests.toLocaleString('en-ZA')}</td><td className="px-4 py-3">{client.lastSeenAt ? formatDate(client.lastSeenAt) : 'Never active'}</td>
  </tr>;
}

function HealthCard({ label, value, detail, tone = 'ink' }: { label: string; value: number; detail: string; tone?: 'ink' | 'clay' }) {
  return <div className="rounded-sm bg-white p-4 shadow-sm ring-1 ring-ink/10"><p className="text-sm text-ink/70">{label}</p><p className={cn('mt-1 text-2xl font-semibold', tone === 'clay' ? 'text-clay' : 'text-ink')}>{value.toLocaleString('en-ZA')}</p><p className="mt-1 text-xs text-ink/60">{detail}</p></div>;
}

function HealthBadge({ children, tone = 'ink' }: { children: React.ReactNode; tone?: 'ink' | 'clay' }) {
  return <span className={cn('inline-flex rounded-full px-2 py-0.5 text-[11px] font-semibold', tone === 'clay' ? 'bg-clay/15 text-clay' : 'bg-ink/10 text-ink/70')}>{children}</span>;
}

function StatusBadge({ enabled, suspended, pending }: { enabled: boolean; suspended: boolean; pending: boolean }) {
  const label = suspended ? 'Suspended' : pending ? 'Invitation pending' : enabled ? 'Enabled' : 'Disabled';
  return <span className={cn('inline-flex rounded-full px-2.5 py-1 text-xs font-semibold', suspended ? 'bg-clay/15 text-clay' : pending ? 'bg-ink/10 text-ink/70' : enabled ? 'bg-moss/15 text-moss' : 'bg-ink/10 text-ink/70')}>{label}</span>;
}
